// @flow
import React, { useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Hidden from '@mui/material/Hidden';

import DisplaySupport from '../SupportDialog';
import UpgradePlanDialog from '../Billing/UpgradePlanDialog';
import RechargeAccountDialog from '../Billing/RechargeAccountDialog';
import DrawerContent from './DrawerContent';
import memberDrawerStyles from '../Styles/MemberDrawer.Style';

import {
  setOpenSupportDialog,
  setUpgradePopup,
  setRechargePopup,
  selectOpenSupportDialog,
  selectOpenPopups
} from '../../reduxStore/modules/drawer';

import {
  fetchPlanInfo,
  refreshBilling,
  selectBillingState,
  selectPlanInfo,
  selectSubscriptionInfo,
  selectSubscriptionId
} from '../../reduxStore/modules/billing';

import { selectUser, selectUserRole } from '../../reduxStore/modules/user';
import { selectIsBillingLive, selectIsAppLoading } from '../../reduxStore/modules/common';
import { selectAccountUsage } from '../../reduxStore/modules/user';
import { useI18n } from '../../contexts/I18nContext';

type Props = {
  mobileOpen: bool,
  usehandleDrawerToggle: Function,
  isLoading?: bool
};

const MemberDrawer = ({
  mobileOpen,
  usehandleDrawerToggle,
  isLoading = false
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const i18n = useI18n();

  // Drawer state selectors - use individual selectors to prevent unnecessary re-renders
  const openSupportDialog = useSelector(selectOpenSupportDialog);
  const openPopups = useSelector(selectOpenPopups);

  // User selectors
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);

  // Billing selectors
  const isBillingLive = useSelector(selectIsBillingLive);
  const billingState = useSelector(selectBillingState);
  const subscriptionId = useSelector(selectSubscriptionId);
  const planInfo = useSelector(selectPlanInfo);
  const subscriptionInfo = useSelector(selectSubscriptionInfo);

  // App loading state - tracks when billing data is being loaded
  const isAppLoading = useSelector(selectIsAppLoading);

  // Use app loading state for billing data, or fall back to prop-based loading
  const isBillingDataLoading = isAppLoading || isLoading;

  // Memoized callbacks
  const getListItemStyles = useCallback((pathSegment) => (theme) => {
    const baseStyle = memberDrawerStyles.listItem(theme);
    const isSelected =  history.location && 
                        history.location.pathname && 
                        history.location.pathname.includes(pathSegment);
    return {
      ...baseStyle,
      ...(isSelected ? memberDrawerStyles.selected(theme) : {})
    };
  }, [history.location]);

  const handleRecharge = useCallback(() => {
    dispatch(setRechargePopup(true));
  }, [dispatch]);

  const handleUpgrade = useCallback(() => {
    history.push('/subscribe');
  }, [history]);

  // Effects
  useEffect(() => {
    // Only fetch if we have user, subscriptionId, billing is live, AND cache is missing
    const hasCache = planInfo && subscriptionInfo;
    if (user && subscriptionId && isBillingLive && !hasCache) {
      dispatch(fetchPlanInfo(subscriptionId, user));
    }
  }, [user, subscriptionId, isBillingLive, planInfo, subscriptionInfo, dispatch]);

  useEffect(() => {
    if (!user) return;

    // Define credit-sensitive routes that need regular billing updates
    const creditSensitiveRoutes = ['/member', '/billing', '/neo'];
    const shouldAutoRefresh = creditSensitiveRoutes.some(
      route => location.pathname.includes(route)
    );

    // Only set up auto-refresh on credit-sensitive routes
    if (!shouldAutoRefresh) return;

    // Check if auto-refresh is disabled in billing state
    if (billingState && billingState.autorefresh === false) return;

    // Set up 5-minute interval instead of 50 seconds
    const interval = setInterval(() => {
      dispatch(refreshBilling());
    }, 5 * 60 * 1000); // 5 minutes = 300,000ms

    return () => clearInterval(interval);
  }, [user, billingState, dispatch, location.pathname]);

  const drawer = (
    <DrawerContent
      userRole={isBillingDataLoading ? null : userRole}
      onRecharge={handleRecharge}
      onUpgrade={handleUpgrade}
      getListItemStyles={getListItemStyles}
      onSupportClick={() => dispatch(setOpenSupportDialog(true))}
      isLoading={isBillingDataLoading}
    />
  );

  return (
    <Box component="nav" sx={(theme) => memberDrawerStyles.drawer(theme)}>
      {/* Mobile drawer */}
      <Hidden mdUp implementation="css">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={usehandleDrawerToggle}
          sx={(theme) => memberDrawerStyles.drawerPaperMobile(theme)}
          ModalProps={{
            keepMounted: true // Better performance for mobile
          }}
        >
          {drawer}
        </Drawer>
      </Hidden>

      {/* Desktop drawer */}
      <Hidden smDown implementation="css">
        <Drawer
          variant="permanent"
          sx={(theme) => memberDrawerStyles.drawerPaper(theme)}
          open
        >
          {drawer}
        </Drawer>
      </Hidden>

      {/* Dialogs */}
      <UpgradePlanDialog
        open={openPopups.upgradePlan}
        setOpen={(open) => dispatch(setUpgradePopup(open))}
      />
      <RechargeAccountDialog
        open={openPopups.rechargeAccount}
        setOpen={(open) => dispatch(setRechargePopup(open))}
      />
    </Box>
  );
};

export default MemberDrawer;